import {
  forwardRef,
  type ChangeEvent,
  type InputHTMLAttributes,
  type WheelEvent,
} from "react";

type NumberInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  /** Displays the value with thousand separators, e.g. 5,000. Native `type="number"` inputs can't render commas, so this switches to a text input under the hood while still emitting a plain numeric string via onChange. */
  thousandSeparator?: boolean;
};

const VALID_NUMERIC_INPUT = /^\d*\.?\d*$/;

function formatWithCommas(raw: string): string {
  if (!raw) return "";
  const [integerPart, decimalPart] = raw.split(".");
  const formattedInteger = integerPart ? Number(integerPart).toLocaleString("en-US") : "";
  return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
}

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ onWheel, onChange, value, thousandSeparator, ...props }, ref) => {
    const handleWheel = (e: WheelEvent<HTMLInputElement>) => {
      e.currentTarget.blur();
      onWheel?.(e);
    };

    if (!thousandSeparator) {
      return (
        <input
          {...props}
          ref={ref}
          type="number"
          value={value}
          onChange={onChange}
          onWheel={handleWheel}
        />
      );
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/,/g, "");
      if (!VALID_NUMERIC_INPUT.test(raw)) return;
      e.target.value = raw;
      onChange?.(e);
    };

    return (
      <input
        {...props}
        ref={ref}
        type="text"
        inputMode="decimal"
        value={formatWithCommas(String(value ?? ""))}
        onChange={handleChange}
        onWheel={handleWheel}
      />
    );
  },
);
NumberInput.displayName = "NumberInput";

export default NumberInput;
